/**
 * Phase 336 wave 2 task 2.3 (#1163) — the Vacancies PAGE MODEL.
 *
 * <p>The role-level forecast payload in, the page's month bands / headline / chart out. No
 * fetch, no React, no clock, no defaults pulled from the environment — the page (task 3.2)
 * reads the wire through `useForecastRoles` and renders what these functions return. The
 * split is what makes the four rules below testable as RULES rather than as pixels.</p>
 *
 * <h3>The four rules this module exists to enforce</h3>
 * <ol>
 *   <li><b>Attribution is UPSTREAM.</b> Every role arrives already attributed to one
 *       consultant by the engine's support-owner rule (`ForecastRolesReadService`, phase 338
 *       AC-2: the first roster owner who is not in the tenant's `support_owner_ids`). This
 *       module renders `loxo_user_id` / `consultant_name` verbatim and NEVER re-derives who
 *       owns a role. Two surfaces re-deriving one attribution rule is how the Teams card and
 *       the portal end up crediting the same vacancy to two different people.</li>
 *   <li><b>Overdue is a PARTITION, not a month.</b> An overdue role keeps its real, PAST
 *       `est_placed_month` and is discriminated by its `overdue` flag
 *       (`ReportingReadModels.cs` ForecastRoleRowDto — "a consumer MUST branch on this flag
 *       and MUST NOT month-group an overdue role into a forecast month"). So
 *       {@link monthGroups} never emits an overdue role and {@link overdueRoles} holds every
 *       one of them: the phase-311 partition (`ForecastReportModel.ForecastOverdueRow` drops
 *       the month entirely) carried into presentation. Re-keying an overdue role to a month
 *       is exactly what put year-old rows in a forecast on card #1072.</li>
 *   <li><b>The headline is A+R; B and C are shown and not counted.</b>
 *       {@link HEADLINE_PRIORITIES} mirrors the engine's own default headline set
 *       (`ForecastReportModel.DefaultHeadlinePriorities` = A, R — the documented ADR-004
 *       default). B rides in the secondary "incl. B" split; C and unclassified roles appear
 *       in every band and count toward no headline at all. A role is never DROPPED for its
 *       priority — an invisible vacancy is a worse error than an uncounted one.</li>
 *   <li><b>Identity comes from the shared primitive.</b> Roster order, the `User {id}`
 *       fallback and the nine categorical colours are `@ardept/design-system`'s
 *       `orderRoster` / `displayName` / `colorFor` — the same module the Sales Activity page
 *       and the Monday Teams card read — so a consultant holds one position, one name and
 *       one hue across every surface. Nothing here invents a palette (the #839 class).</li>
 * </ol>
 *
 * <h3>Two calls this module makes, recorded for the AC-5 bar</h3>
 * <ul>
 *   <li><b>The headline counts EVERY A+R role, overdue included</b> — phase 578 (card #2167)
 *       reversed card #1846 decision 4, the phase-501 call that kept overdue value outside
 *       the A+R figure. A role past its expected month is still a live vacancy, and the
 *       client's own tracker counts it, so {@link headline} sums every role and
 *       {@link VacanciesHeadline.overdue} reports the overdue partition as a SLICE of the
 *       headline: its A+R £ is the slice, its count spans every letter — the same slice the
 *       Monday card's Overdue tile shows. Overdue is still a partition of the LIST and the
 *       chart (rule 2); only the headline arithmetic crosses it. <b>The property given up,
 *       recorded rather than hidden:</b> the headline no longer equals the engine's own
 *       `forecast_value` facts. Those exclude the overdue rollup by construction
 *       (`ForecastReport.ConsolidatedOverdue` is a separate segment), and that rollup carries
 *       no priority, so the overdue A+R slice is not reconstructable from the fact store alone
 *       — the consequence ADR-088 D2 records. What holds instead is parity with the card: its
 *       "Current Pipeline" is the same Σ over the same role rows by the same A+R cut.</li>
 *   <li><b>Money formatting lives here, not in the page.</b> {@link formatGbp} is the one £
 *       formatter for this view; the interim page's local copy
 *       (`screens/VacanciesPage.tsx`) is retired by the task 3.2 rewrite that consumes this
 *       module. GBP is a display-layer choice for the UK pilot — the fact store carries no
 *       currency dimension, so there is nothing on the wire to switch on, and a non-GBP
 *       tenant needs a currency dimension first rather than a guess here.</li>
 * </ul>
 */
import {
  colorFor,
  displayName,
  orderRoster,
  parsePriority,
  type Priority,
  type StackedBarSeries,
} from "@ardept/design-system";
import type { ForecastRoleRow, ForecastRolesResponse } from "../api/types.js";

// ── Vocabulary ──────────────────────────────────────────────────────────────────────────

/**
 * The priorities that count toward the headline value, in headline order.
 *
 * <p>The web twin of `ForecastReportModel.DefaultHeadlinePriorities` (`["A", "R"]`) — the
 * forecast family's documented ADR-004 default.</p>
 *
 * <p><b>Known boundary, recorded rather than hidden.</b> The engine lets a tenant override
 * the set via `tenant_config.reporting.forecast.headline_priorities`, but the role-level
 * payload does NOT carry the resolved set on the wire (`ForecastRolesDto` is roles + as_of +
 * priority_enabled). So this page honours the DEFAULT, and a tenant that overrides it would
 * need the field added to the DTO and mirrored here — a wire change, not a portal tweak. No
 * tenant overrides it today.</p>
 */
export const HEADLINE_PRIORITIES: readonly Priority[] = ["A", "R"];

/**
 * The priority that joins A+R in the secondary "incl. B" figure.
 *
 * <p>C is deliberately NOT here: the split the design shows is "A+R" against "incl. B", and
 * a C role is visible in its month band while counting toward neither figure. C has zero
 * instances in the pilot data today and is in the vocabulary per the ask-pin, so it is
 * handled explicitly rather than left to fall through a `default`.</p>
 */
export const INCL_B_PRIORITY: Priority = "B";

/** The headline's caption — what the big number is. */
export const HEADLINE_CAPTION = "A + R";

/** The secondary split's caption. */
export const INCL_B_CAPTION = "incl. B";

/** The heading of the overdue treatment. Overdue roles are never under a month band. */
export const OVERDUE_LABEL = "Overdue";

/** Long month heading form, for the band headings. */
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ── Formatting ──────────────────────────────────────────────────────────────────────────

/**
 * ONE formatter instance — constructing an `Intl.NumberFormat` is the expensive part, and a
 * page paint formats one per role row plus the axis ticks.
 *
 * <p>`maximumFractionDigits: 0` because the facts are whole pounds (`est_value` is a C#
 * `int`); pence here would be invented precision.</p>
 */
const GBP_FORMAT = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

/** A whole-pound amount as a client reads it (210000 → "£210,000"). */
export function formatGbp(value: number): string {
  return GBP_FORMAT.format(value);
}

/**
 * `"2026-08"` → `"August 2026"` — the month band heading.
 *
 * <p>Built from a fixed table rather than `toLocaleDateString`, for the reason
 * `salesActivityModel.monthLabel` gives: the heading must read identically on every machine,
 * and constructing a `Date` from a `yyyy-MM` string invites a timezone off-by-one that
 * renders August as July west of Greenwich. An unrecognised input is returned UNCHANGED, so
 * a malformed month is visible rather than folded into a real one.</p>
 */
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return month;
  const name = MONTH_NAMES[Number(match[2]) - 1];
  if (name === undefined) return month;
  return `${name} ${match[1]}`;
}

/**
 * `"2026-08"` → `"Aug 2026"` — the CHART x-axis form.
 *
 * <p>Short because a stacked column chart puts every category label on one rail: six long
 * months collide, and a collided axis label is worse than an abbreviated one. Same fallback
 * as {@link monthLabel}.</p>
 */
export function shortMonthLabel(month: string): string {
  const long = monthLabel(month);
  if (long === month) return month;
  const [name, year] = long.split(" ");
  return `${name!.slice(0, 3)} ${year!}`;
}

/**
 * The row's title, or `Vacancy {job_id}` when the provider omits one.
 *
 * <p>The family's no-anonymous-row rule, the same shape as `displayName`'s `User {id}`: a
 * blank cell in a list of vacancies reads as a rendering bug, while a titled-by-id row reads
 * as a vacancy whose title has not been filled in — which is what it is.</p>
 */
export function vacancyTitle(role: ForecastRoleRow): string {
  const title = role.title;
  if (title != null && title.trim().length > 0) return title;
  return `Vacancy ${role.job_id}`;
}

// ── The projected role row ──────────────────────────────────────────────────────────────

/** One vacancy as the page renders it — a month band row, or an overdue row. */
export interface VacancyRow {
  /** Loxo `job.id` — the row's React key and its identity in the payload. */
  jobId: number;
  /** {@link vacancyTitle}: the provider's title, or `Vacancy {job_id}`. */
  title: string;
  /** The hiring company, verbatim from the provider. `null` when it omits one. */
  companyName: string | null;
  /**
   * The attributed consultant's id as a STRING. The wire carries it as a JSON number here
   * while the roster and weekly-grid carry the same id as a string, so the conversion is one
   * documented place rather than a silent always-miss lookup.
   */
  loxoUserId: string;
  /** `displayName` of the attributed consultant — `User {id}` when the roster cannot name them. */
  consultantName: string;
  /** `var(--ard-cat-N)`, from the consultant's roster index — the SAME hue the chart uses. */
  consultantColorVar: string;
  /** A|R|B|C, or `null` when the tenant has priority ingestion off / the role is unclassified. */
  priority: Priority | null;
  /** True when this role counts toward {@link VacanciesHeadline.headlineValue}. */
  countsToHeadline: boolean;
  /** Estimated placement value, whole £. */
  estValue: number;
  /** {@link formatGbp} of {@link estValue}. */
  estValueLabel: string;
  /** `yyyy-MM` — the wire's OWN month key. Never re-derived from {@link estPlacedDate}. */
  estPlacedMonth: string;
  /** `yyyy-MM-dd`, the estimated placement date. */
  estPlacedDate: string;
  /** The wire's overdue discriminator. True ⇒ this row is in the overdue treatment. */
  overdue: boolean;
}

/**
 * Narrows the wire's `priority` to the closed A|R|B|C vocabulary.
 *
 * <p>`null` passes through as `null` — an unclassified role is SHOWN and uncounted, which is
 * an ordinary state (a tenant with priority ingestion off has null on every row). Anything
 * else THROWS, at the projection boundary with the role in hand: an unrecognised letter means
 * the wire mirror has drifted, and a quietly-defaulted chip would misreport a vacancy's
 * priority to the client. Same fail-loud posture as the priority ingestion it mirrors.</p>
 */
function rolePriority(role: ForecastRoleRow): Priority | null {
  if (role.priority === null) return null;
  try {
    return parsePriority(role.priority);
  } catch (cause) {
    throw new Error(
      `vacanciesModel: role ${role.job_id} carries an unusable priority — ` +
        (cause instanceof Error ? cause.message : String(cause)),
    );
  }
}

/**
 * The consultant identity map: every consultant PRESENT in the payload, in roster order
 * (`loxo_user_id` ASC), each pinned to their categorical colour.
 *
 * <p>Built over EVERY role — overdue included — on purpose. The role rows show all roles
 * while the chart shows only the forecast partition; indexing those two sets separately would
 * give one consultant two different colours on one page the moment their only overdue role
 * dropped them out of the chart's set. One index map, one colour.</p>
 *
 * <p>Ordering is `orderRoster`, never the payload's own order: the payload's order is the
 * compute's, which is deterministic but not stable across weeks as roles come and go, and a
 * consultant whose hue changed week to week would make the chart unreadable.</p>
 */
interface ConsultantIdentity {
  loxoUserId: string;
  name: string;
  colorVar: string;
  /** 0-based roster index — the series order for the chart. */
  index: number;
}

function consultantIdentities(
  roles: readonly ForecastRoleRow[],
): Map<string, ConsultantIdentity> {
  const seen = new Map<string, { loxoUserId: string; name: string | null }>();
  for (const role of roles) {
    const loxoUserId = String(role.loxo_user_id);
    // First non-blank name wins; a later row that lost its name must not blank an earlier one.
    const existing = seen.get(loxoUserId);
    if (existing === undefined) {
      seen.set(loxoUserId, { loxoUserId, name: role.consultant_name });
    } else if (
      (existing.name === null || existing.name.trim().length === 0) &&
      role.consultant_name != null
    ) {
      existing.name = role.consultant_name;
    }
  }

  const identities = new Map<string, ConsultantIdentity>();
  orderRoster([...seen.values()]).forEach((row, index) => {
    identities.set(row.loxoUserId, {
      loxoUserId: row.loxoUserId,
      name: displayName(row),
      colorVar: colorFor(index),
      index,
    });
  });
  return identities;
}

function projectRole(
  role: ForecastRoleRow,
  identities: Map<string, ConsultantIdentity>,
): VacancyRow {
  const loxoUserId = String(role.loxo_user_id);
  const identity = identities.get(loxoUserId);
  // Unreachable — `identities` is built from these same rows — but a thrown lookup miss is
  // better than painting an unknown consultant with slot 1's hue.
  if (identity === undefined) {
    throw new Error(
      `vacanciesModel: role ${role.job_id} names consultant ${loxoUserId}, ` +
        "who is absent from the identity map built from the same payload",
    );
  }
  const priority = rolePriority(role);
  return {
    jobId: role.job_id,
    title: vacancyTitle(role),
    companyName: role.company_name,
    loxoUserId,
    consultantName: identity.name,
    consultantColorVar: identity.colorVar,
    priority,
    countsToHeadline: priority !== null && HEADLINE_PRIORITIES.includes(priority),
    estValue: role.est_value,
    estValueLabel: formatGbp(role.est_value),
    estPlacedMonth: role.est_placed_month,
    estPlacedDate: role.est_placed_date,
    overdue: role.overdue,
  };
}

/**
 * The order roles take WITHIN a band: soonest first, then by job id.
 *
 * <p>A total, payload-order-independent order — feeding the same roles in any array order
 * returns the same sequence, which is what stops a band silently reshuffling between two
 * paints of identical data. Sorting by value or by priority instead would turn a delivery
 * schedule into a league table, which the family's fixed-roster posture deliberately avoids.</p>
 */
function byDateThenJob(a: VacancyRow, b: VacancyRow): number {
  if (a.estPlacedDate !== b.estPlacedDate) return a.estPlacedDate < b.estPlacedDate ? -1 : 1;
  return a.jobId - b.jobId;
}

// ── monthGroups: the forecast bands ─────────────────────────────────────────────────────

/** One calendar month band of the Vacancies list. */
export interface MonthGroup {
  /** `yyyy-MM` — the wire's own month key, carried so a test/page can join without parsing. */
  month: string;
  /** {@link monthLabel}: `August 2026` — the band heading. */
  label: string;
  /** The band's roles, soonest first. NEVER an overdue role. */
  roles: VacancyRow[];
  /** Σ `est_value` of the roles in the band — every priority, matching what the band shows. */
  value: number;
  /** {@link formatGbp} of {@link value}. */
  valueLabel: string;
  /** Σ `est_value` of the band's A+R roles — the band's contribution to the headline. */
  headlineValue: number;
  /** {@link formatGbp} of {@link headlineValue}. */
  headlineValueLabel: string;
}

/**
 * The forecast month bands: NON-OVERDUE roles grouped by their own `est_placed_month`,
 * months ASCENDING.
 *
 * <p>Ascending because the list is a delivery schedule read forwards — next month first, the
 * far end last. (The Sales Activity table reads the other way, newest-first, because it
 * answers "how did we just do"; the two orders are deliberate and each is pinned by a test.)</p>
 *
 * <p><b>Overdue roles are not here and cannot be.</b> They are the other half of the
 * partition ({@link overdueRoles}), and grouping one under its real past month would put a
 * year-old row in a forward forecast — the #1072 failure. The two functions together contain
 * every role in the payload exactly once, which the tests assert directly rather than by
 * inspection.</p>
 *
 * <p>A month with no roles simply does not appear: bands come from the roles, so there is no
 * zero-valued band to suppress and no gap band to invent. (Contrast the weekly table, where
 * an ABSENT week between recorded weeks is a labelled gap — a forecast has no such thing,
 * because a month nobody expects to place in is not a measurement that went missing.)</p>
 */
export function monthGroups(payload: ForecastRolesResponse | null | undefined): MonthGroup[] {
  const roles = payload?.roles ?? [];
  const identities = consultantIdentities(roles);

  const byMonth = new Map<string, VacancyRow[]>();
  for (const role of roles) {
    if (role.overdue) continue;
    const row = projectRole(role, identities);
    const bucket = byMonth.get(row.estPlacedMonth);
    if (bucket === undefined) byMonth.set(row.estPlacedMonth, [row]);
    else bucket.push(row);
  }

  return [...byMonth.keys()]
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .map((month) => {
      const groupRoles = byMonth.get(month)!.sort(byDateThenJob);
      const value = groupRoles.reduce((sum, row) => sum + row.estValue, 0);
      const headlineValue = groupRoles
        .filter((row) => row.countsToHeadline)
        .reduce((sum, row) => sum + row.estValue, 0);
      return {
        month,
        label: monthLabel(month),
        roles: groupRoles,
        value,
        valueLabel: formatGbp(value),
        headlineValue,
        headlineValueLabel: formatGbp(headlineValue),
      };
    });
}

// ── overdueRoles: the other half of the partition ───────────────────────────────────────

/** The overdue treatment's model — the roles no month band may hold. */
export interface OverdueGroup {
  /** {@link OVERDUE_LABEL} — the treatment's heading. */
  label: string;
  /** Every role the payload flagged overdue, OLDEST first. Empty when there are none. */
  roles: VacancyRow[];
  /** Σ `est_value` of {@link roles}. */
  value: number;
  /** {@link formatGbp} of {@link value}. */
  valueLabel: string;
  /** Σ `est_value` of the overdue A+R roles. */
  headlineValue: number;
  /** {@link formatGbp} of {@link headlineValue}. */
  headlineValueLabel: string;
}

/**
 * The overdue partition: every role the payload flagged, rendered INLINE in the design's
 * overdue treatment and never under a calendar band.
 *
 * <p>Oldest first — the longest-overdue vacancy is the one that needs looking at. Each row
 * still carries its real `estPlacedMonth` and `estPlacedDate`, because that is the
 * information the treatment shows ("expected July 2025"); what it must never do is become a
 * grouping key. The flag is the discriminator, exactly as the wire mirror's consumer contract
 * requires.</p>
 *
 * <p>An empty payload yields an empty group rather than `null`, so the page branches on
 * `roles.length` alone and cannot render an "Overdue" heading with nothing under it by
 * forgetting a null check.</p>
 */
export function overdueRoles(payload: ForecastRolesResponse | null | undefined): OverdueGroup {
  const roles = payload?.roles ?? [];
  const identities = consultantIdentities(roles);

  const rows = roles
    .filter((role) => role.overdue)
    .map((role) => projectRole(role, identities))
    .sort(byDateThenJob);

  const value = rows.reduce((sum, row) => sum + row.estValue, 0);
  const headlineValue = rows
    .filter((row) => row.countsToHeadline)
    .reduce((sum, row) => sum + row.estValue, 0);

  return {
    label: OVERDUE_LABEL,
    roles: rows,
    value,
    valueLabel: formatGbp(value),
    headlineValue,
    headlineValueLabel: formatGbp(headlineValue),
  };
}

// ── headline: A+R, with the incl-B split ────────────────────────────────────────────────

/** How many roles sit in each priority band — the headline's sub-caption. */
export interface PriorityCounts {
  A: number;
  R: number;
  B: number;
  C: number;
  /** Roles with `priority: null` — shown, counted toward no headline. */
  unclassified: number;
}

/** The Vacancies headline block. */
export interface VacanciesHeadline {
  /**
   * THE number: Σ `est_value` of EVERY role priced A or R — forecast AND overdue.
   *
   * <p>An overdue A+R role is INSIDE it (phase 578, card #2167), so {@link overdue} is a slice
   * of this figure rather than a figure outside it. The portal-side invariant is
   * `headlineValue == Σ monthGroups(payload)[].headlineValue + overdueRoles(payload).headlineValue`
   * — the mirror of the engine's `PipelineForecastValue == TotalForecastValue +
   * Σ UpcomingMonths[].ForecastValue + OverdueHeadlineValue`. The price of that: it no longer
   * equals the engine's `forecast_value` facts, which exclude the overdue rollup (see
   * {@link headline}).</p>
   *
   * <p>Zero when {@link priorityEnabled} is false — every priority is null then, so there is
   * no A+R set to sum. The page renders the no-priority view off that flag rather than
   * showing a client a confident `£0`.</p>
   */
  headlineValue: number;
  /** {@link formatGbp} of {@link headlineValue}. */
  headlineLabel: string;
  /** The caption above it: {@link HEADLINE_CAPTION}. */
  headlineCaption: string;
  /**
   * Σ `est_value` of every role priced A, R or B, overdue included — always ≥
   * {@link headlineValue}, because both span the same roles.
   */
  inclBValue: number;
  /** {@link formatGbp} of {@link inclBValue}. */
  inclBLabel: string;
  /** The secondary split's caption: {@link INCL_B_CAPTION}. */
  inclBCaption: string;
  /** Σ `est_value` of EVERY role — C, unclassified and overdue included. */
  forecastValue: number;
  /** {@link formatGbp} of {@link forecastValue}. */
  forecastLabel: string;
  /**
   * The overdue partition's totals — a SLICE of the figures above, never a figure outside
   * them (phase 578, card #2167). {@link count} and {@link value} span every letter;
   * {@link headlineValue} is the A+R slice of the headline, the £ the Monday card's Overdue
   * tile shows.
   */
  overdue: {
    /** Overdue roles of EVERY letter. */
    count: number;
    /** Σ `est_value` of the overdue roles of every letter — the slice of {@link forecastValue}. */
    value: number;
    valueLabel: string;
    /** Σ of the overdue A+R roles — the slice of {@link headlineValue} the overdue roles are. */
    headlineValue: number;
    headlineValueLabel: string;
  };
  /** Role counts per priority band, across the whole payload (overdue included). */
  counts: PriorityCounts;
  /** Total roles in the payload — forecast plus overdue. */
  roleCount: number;
  /** The wire's `priority_enabled`: false ⇒ every `priority` is null for this tenant. */
  priorityEnabled: boolean;
  /** The compute's freshness disclosure, passed through for the page's "data as at" line. */
  asOf: string | null;
}

/**
 * The headline block: A+R prominent, "incl. B" secondary, everything else visible and
 * uncounted.
 *
 * <p><b>All three figures span EVERY role, overdue included</b> (phase 578, card #2167, which
 * reversed card #1846 decision 4). An overdue role stays out of the month bands and the chart —
 * overdue is a partition of the LIST (rule 2, #1072) — but it is still a live vacancy, so its
 * value counts, and {@link VacanciesHeadline.overdue} reports the slice of these figures the
 * overdue partition contributes. Only the arithmetic here crossed the partition:
 * {@link monthGroups}, {@link overdueRoles} and {@link stackedChartData} did not move, and
 * `headlineValue == Σ monthGroups[].headlineValue + overdueRoles.headlineValue` holds by
 * construction.</p>
 *
 * <p><b>Given up, deliberately:</b> {@link headlineValue} no longer equals the engine's own
 * `forecast_value` facts. Those exclude the overdue rollup by construction
 * (`ForecastReport.ConsolidatedOverdue` is its own segment), and that rollup carries no
 * priority, so the overdue A+R slice is not reconstructable from the fact store alone —
 * ADR-088 D2 records the consequence. What this figure DOES equal is the Monday card's
 * "Current Pipeline": the engine sums the same role rows by the same A+R cut, and one
 * cross-surface fixture pins both suites to one number.</p>
 *
 * <p>B is EXCLUDED from {@link headlineValue} and INCLUDED in {@link inclBValue}; C is in
 * neither, while its roles stay visible in their bands with their chip. Unclassified roles
 * (`priority: null`) behave like C. Nothing is dropped for its priority anywhere in this
 * module — {@link forecastValue} is the sum of every role, so the page can always show what
 * the bands and the overdue table add up to.</p>
 */
export function headline(payload: ForecastRolesResponse | null | undefined): VacanciesHeadline {
  const roles = payload?.roles ?? [];
  const identities = consultantIdentities(roles);
  const rows = roles.map((role) => projectRole(role, identities));

  const counts: PriorityCounts = { A: 0, R: 0, B: 0, C: 0, unclassified: 0 };
  for (const row of rows) {
    if (row.priority === null) counts.unclassified += 1;
    else counts[row.priority] += 1;
  }

  const overdue = rows.filter((row) => row.overdue);
  const sum = (subset: readonly VacancyRow[]): number =>
    subset.reduce((total, row) => total + row.estValue, 0);

  // Over EVERY row: the overdue partition is a slice of these figures, not a cut from them.
  const headlineValue = sum(rows.filter((row) => row.countsToHeadline));
  const inclBValue = sum(
    rows.filter((row) => row.countsToHeadline || row.priority === INCL_B_PRIORITY),
  );
  const forecastValue = sum(rows);
  const overdueValue = sum(overdue);
  const overdueHeadlineValue = sum(overdue.filter((row) => row.countsToHeadline));

  return {
    headlineValue,
    headlineLabel: formatGbp(headlineValue),
    headlineCaption: HEADLINE_CAPTION,
    inclBValue,
    inclBLabel: formatGbp(inclBValue),
    inclBCaption: INCL_B_CAPTION,
    forecastValue,
    forecastLabel: formatGbp(forecastValue),
    overdue: {
      count: overdue.length,
      value: overdueValue,
      valueLabel: formatGbp(overdueValue),
      headlineValue: overdueHeadlineValue,
      headlineValueLabel: formatGbp(overdueHeadlineValue),
    },
    counts,
    roleCount: rows.length,
    priorityEnabled: payload?.priority_enabled ?? false,
    asOf: payload?.as_of ?? null,
  };
}

// ── stackedChartData: months × consultants ──────────────────────────────────────────────

/** The `StackedBarChart` input, plus the month keys the labels were built from. */
export interface StackedChartData {
  /** The x labels, oldest → newest — {@link shortMonthLabel} of {@link months}. */
  categories: string[];
  /** The `yyyy-MM` keys behind {@link categories}, index-aligned. */
  months: string[];
  /** One series per consultant, in ROSTER order, values index-aligned to {@link categories}. */
  series: StackedBarSeries[];
}

/**
 * The monthly stacked value chart: forecast months across, consultants stacked.
 *
 * <p><b>Overdue roles are excluded</b>, for the same reason they get no month band: a column
 * for a past month in a forward forecast is the #1072 bug drawn in SVG. The overdue treatment
 * carries their value instead.</p>
 *
 * <p><b>Series order is roster order</b> (`loxo_user_id` ASC), and each series wears the
 * colour from the SAME identity map the role rows read, so the swatch beside a consultant's
 * name and their band in the chart can never be different hues. The order is fixed regardless
 * of the month's numbers: sorting segments by size would recolour the chart whenever the data
 * moved, and a reader who learned "Ellie is the bottom band" could not read next month's.</p>
 *
 * <p><b>Every consultant gets a series for every month</b>, zero-filled where they have no
 * roles — the phase-334 zero-segment call the primitive documents: a consultant with a quiet
 * month keeps their legend entry and their slot, so the chart's cast is identical month to
 * month. `StackedBarChart` renders those at zero height.</p>
 *
 * <p>An empty forecast yields empty arrays, which the primitive renders as `null` — the page
 * owns the empty state, because only the page can say something useful about why there is
 * nothing to show.</p>
 */
export function stackedChartData(
  payload: ForecastRolesResponse | null | undefined,
): StackedChartData {
  const groups = monthGroups(payload);
  const identities = consultantIdentities(payload?.roles ?? []);
  const months = groups.map((group) => group.month);

  // Only consultants with at least one FORECAST role take a series: a consultant whose whole
  // presence in the payload is overdue has nothing to stack, and an all-zero series would put
  // a name in the legend that no column ever shows.
  const totals = new Map<string, number[]>();
  groups.forEach((group, monthIndex) => {
    for (const row of group.roles) {
      let values = totals.get(row.loxoUserId);
      if (values === undefined) {
        values = new Array<number>(months.length).fill(0);
        totals.set(row.loxoUserId, values);
      }
      values[monthIndex]! += row.estValue;
    }
  });

  const series = [...totals.entries()]
    .map(([loxoUserId, values]) => ({ identity: identities.get(loxoUserId)!, values }))
    .sort((a, b) => a.identity.index - b.identity.index)
    .map(({ identity, values }) => ({
      key: identity.loxoUserId,
      label: identity.name,
      colorVar: identity.colorVar,
      values,
    }));

  return {
    categories: months.map(shortMonthLabel),
    months,
    series,
  };
}
